import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, setRedirectAfterLogin } from "../lib/session"; // Ensure getToken is imported
import { savePostIntent } from "../lib/postIntent";

const API_BASE = process.env.REACT_APP_API || "http://localhost:4500/api";
const BRANDS_ENDPOINT = `${API_BASE}/brands/user`;

export default function CreateNewPopover({ onClose }) {
  const navigate = useNavigate();

  const [brands, setBrands] = useState([]);  // State for brands
  const [selectedBrand, setSelectedBrand] = useState("");  // Selected brand
  const [text, setText] = useState("");  // Content generation text input

  const [loading, setLoading] = useState(false);  // Loading state

  // Fetch the brands for the logged-in user
  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const token = getToken(); // Get token for authorization
        if (!token) {
          console.log("User not logged in.");
          setLoading(false);
          return;
        }

        const res = await fetch(BRANDS_ENDPOINT, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          console.error("Failed to fetch brands:", res.status);
          setBrands([]);
          setLoading(false);
          return;
        }

        const data = await res.json();
        setBrands(data);  // Set the brands fetched from the backend
        setLoading(false);
      } catch (error) {
        console.error("Error fetching brands:", error);
        setBrands([]);
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const brandExists = brands.length > 0; // Check if brands exist

  // Save selected brand to localStorage
  useEffect(() => {
    if (selectedBrand) {
      localStorage.setItem("onboarding.selectedBrand", selectedBrand);  // Save selected brand ID to localStorage
    }
  }, [selectedBrand]);

  const goOrOnboard = (path) => {
    if (!brandExists) return navigate("/onboarding/step-1");
    navigate(path);
  };

  const goGenerateMultiple = () => goOrOnboard("/bulk-generate");
  const goTemplate = () => goOrOnboard("/templates");
  const goFootage = () => goOrOnboard("/assets/upload");

  const handleGenerate = () => {
    const v = (text || "").trim();
    if (!v) return;

    // Require brand profile before generating
    if (!selectedBrand) {
      alert("Please select a brand before generating content.");
      return;
    }

    // Normalize intent (topic vs URL)
    const isUrl = /^https?:\/\//i.test(v);
    const intent = isUrl ? { type: "url", value: v } : { type: "topic", value: v };

    // 1) Save intent so we can resume after auth
    savePostIntent(intent);

    // 2) Save where to go after login
    localStorage.setItem("redirectAfterLogin", "/generation");

    // Check if the user is logged in before redirecting
    const token = getToken();
    if (!token) {
      // If not logged in, redirect to register page
      setRedirectAfterLogin("/generation");
      navigate(`/register?redirectTo=${encodeURIComponent("/generation")}`);
    } else {
      // If logged in, just proceed
      navigate("/generation");
    }
  };

  return (
    <div className="absolute z-50 top-12 right-0 w-[420px] bg-white rounded-2xl shadow-xl border border-slate-100">
      <div className="p-5">
        <div className="text-sm text-slate-500 mb-2">Generate a new AI item</div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Describe your content topic OR paste a website URL"
          className="w-full min-h-[110px] border border-slate-200 rounded-xl p-3 outline-none focus:ring-2 focus:ring-sky-200"
        />

        {/* Brand Selection Dropdown */}
        <div className="mt-3">
          <label htmlFor="brand-select" className="text-sm text-slate-500">Select Brand:</label>
          <select
            id="brand-select"
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            className="w-full mt-1 border border-slate-200 rounded-xl p-3 outline-none focus:ring-2 focus:ring-sky-200"
            disabled={!brandExists}
          >
            {brands.length === 0 ? (
              <option value="">No brands available</option>
            ) : (
              brands.map((brand) => (
                <option key={brand._id} value={brand._id}>
                  {brand.businessName || brand.name}
                </option>
              ))
            )}
          </select>
        </div>

        <button
          onClick={handleGenerate}
          className="mt-3 w-full h-11 rounded-full bg-sky-500 text-white font-semibold hover:opacity-95"
        >
          Generate
        </button>

        <div className="mt-4 flex items-center justify-between text-sm">
          <button onClick={() => navigate("/brands")} className="text-sky-600 hover:underline">
            Manage brands
          </button>
          <button onClick={() => navigate("/onboarding/step-1")} className="text-sky-600 hover:underline">
            Add new brand +
          </button>
        </div>
      </div>
    </div>
  );
}
